import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
const overlayVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20
  }
};
function ModalLayout({
  title,
  children,
  onClose,
  isOpen
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  isOpen: boolean;
}) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <motion.div variants={overlayVariants} initial="hidden" animate="visible" exit="exit" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-206">
      <motion.div variants={modalVariants} transition={{
      duration: 0.3
    }} className="relative w-full max-w-4xl max-h-[90vh] bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-207">
        <div className="flex items-center justify-between p-6 border-b border-slate-800 bg-slate-900 z-10" data-id="element-208">
          <h2 className="text-2xl font-bold text-white" data-id="element-209">{title}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors rounded-full hover:bg-slate-800" aria-label="Close" data-id="element-210">
            <XIcon className="w-6 h-6" data-id="element-211" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 sm:p-10 text-slate-300 leading-relaxed space-y-6" data-id="element-212">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
export function ImpressumModal({
  isOpen,
  onClose
}: ModalProps) {
  if (!isOpen) return null;
  return <ModalLayout title="Impressum" onClose={onClose} isOpen={isOpen} data-id="element-213">
      <div className="space-y-8" data-id="element-214">
        <section data-id="element-215">
          <h3 className="text-xl font-semibold text-white mb-4" data-id="element-216">Adresse</h3>
          <p data-id="element-217">CRM Kraft LLC</p>
          <p data-id="element-218">Vertreten durch: Justus Kraft</p>
          <p data-id="element-219">7901 4th St. N, Ste. 300</p>
          <p data-id="element-220">St. Petersburg, FL 33702</p>
          <p data-id="element-221">United States of America</p>
        </section>

        <section data-id="element-222">
          <h3 className="text-xl font-semibold text-white mb-4" data-id="element-223">Kontakt</h3>
          <p data-id="element-224">Telefon: +49 157 35999071</p>
          <p data-id="element-225">E-Mail: justus@crm-kraft.de</p>
        </section>

        <section data-id="element-226">
          <h3 className="text-xl font-semibold text-white mb-4" data-id="element-227">
            EU-Streitschlichtung
          </h3>
          <p data-id="element-228">
            Die Europäische Kommission stellt eine Plattform zur
            Online-Streitbeilegung (OS) bereit:
            <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 ml-1" data-id="element-229">
              https://ec.europa.eu/consumers/odr/
            </a>
            .
          </p>
          <p className="mt-2" data-id="element-230">
            Unsere E-Mail-Adresse finden Sie oben im Impressum.
          </p>
        </section>

        <section data-id="element-231">
          <h3 className="text-xl font-semibold text-white mb-4" data-id="element-232">
            Verbraucherstreitbeilegung/Universalschlichtungsstelle
          </h3>
          <p data-id="element-233">
            Wir sind nicht bereit oder verpflichtet, an
            Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
            teilzunehmen.
          </p>
        </section>
      </div>
    </ModalLayout>;
}
export function DatenschutzModal({
  isOpen,
  onClose
}: ModalProps) {
  if (!isOpen) return null;
  return <ModalLayout title="Datenschutzerklärung" onClose={onClose} isOpen={isOpen} data-id="element-234">
      <div className="space-y-10 text-sm sm:text-base" data-id="element-235">
        <section className="space-y-4" data-id="element-236">
          <h3 className="text-xl font-bold text-white" data-id="element-237">
            1. Datenschutz auf einen Blick
          </h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-238">
            Allgemeine Hinweise
          </h4>
          <p data-id="element-239">
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was
            mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website
            besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie
            persönlich identifiziert werden können. Ausführliche Informationen
            zum Thema Datenschutz entnehmen Sie unserer unter diesem Text
            aufgeführten Datenschutzerklärung.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-240">
            Datenerfassung auf dieser Website
          </h4>

          <p className="font-medium text-white" data-id="element-241">
            Wer ist verantwortlich für die Datenerfassung auf dieser Website?
          </p>
          <p data-id="element-242">
            Die Datenverarbeitung auf dieser Website erfolgt durch den
            Websitebetreiber. Dessen Kontaktdaten können Sie dem Abschnitt
            „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung
            entnehmen.
          </p>

          <p className="font-medium text-white" data-id="element-243">Wie erfassen wir Ihre Daten?</p>
          <p data-id="element-244">
            Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese
            mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie in
            ein Kontaktformular eingeben.
          </p>
          <p data-id="element-245">
            Andere Daten werden automatisch oder nach Ihrer Einwilligung beim
            Besuch der Website durch unsere IT Systeme erfasst. Das sind vor
            allem technische Daten (z. B. Internetbrowser, Betriebssystem oder
            Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt
            automatisch, sobald Sie diese Website betreten.
          </p>

          <p className="font-medium text-white" data-id="element-246">Wofür nutzen wir Ihre Daten?</p>
          <p data-id="element-247">
            Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung
            der Website zu gewährleisten. Andere Daten können zur Analyse Ihres
            Nutzerverhaltens verwendet werden.
          </p>

          <p className="font-medium text-white" data-id="element-248">
            Welche Rechte haben Sie bezüglich Ihrer Daten?
          </p>
          <p data-id="element-249">
            Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft,
            Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu
            erhalten. Sie haben außerdem ein Recht, die Berichtigung oder
            Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur
            Datenverarbeitung erteilt haben, können Sie diese Einwilligung
            jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht,
            unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer
            personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein
            Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
          </p>
          <p data-id="element-250">
            Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie
            sich jederzeit an uns wenden.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-251">
            Analyse-Tools und Tools von Drittanbietern
          </h4>
          <p data-id="element-252">
            Beim Besuch dieser Website kann Ihr Surf-Verhalten statistisch
            ausgewertet werden. Das geschieht vor allem mit sogenannten
            Analyseprogrammen.
          </p>
          <p data-id="element-253">
            Detaillierte Informationen zu diesen Analyseprogrammen finden Sie in
            der folgenden Datenschutzerklärung.
          </p>
        </section>

        <section className="space-y-4" data-id="element-254">
          <h3 className="text-xl font-bold text-white" data-id="element-255">2. Hosting</h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-256">Webflow</h4>
          <p data-id="element-257">
            Wir hosten unsere Website bei Webflow. Anbieter ist die Webflow,
            Inc., 398 11th Street, 2nd Floor, San Francisco, CA 94103, USA
            (nachfolgend Webflow). Wenn Sie unsere Website besuchen, erfasst
            Webflow verschiedene Logfiles inklusive Ihrer IP-Adressen.
          </p>
          <p data-id="element-258">
            Webflow ist ein Tool zum Erstellen und zum Hosten von Websites.
            Webflow speichert Cookies oder sonstige
            Wiedererkennungstechnologien, die für die Darstellung der Seite, zur
            Bereitstellung bestimmter Webseitenfunktionen und zur Gewährleistung
            der Sicherheit erforderlich sind (notwendige Cookies).
          </p>
          <p data-id="element-259">
            Details entnehmen Sie der Datenschutzerklärung von Webflow:{' '}
            <a href="https://webflow.com/legal/eu-privacy-policy" className="text-orange-400 hover:text-orange-300" data-id="element-260">
              https://webflow.com/legal/eu-privacy-policy
            </a>
            .
          </p>
          <p data-id="element-261">
            Die Verwendung von Webflow erfolgt auf Grundlage von Art. 6 Abs. 1
            lit. f DSGVO. Wir haben ein berechtigtes Interesse an einer
            möglichst zuverlässigen Darstellung unserer Website. Sofern eine
            entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung
            ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25
            Abs. 1 TTDSG, soweit die Einwilligung die Speicherung von Cookies
            oder den Zugriff auf Informationen im Endgerät des Nutzers (z. B.
            Device-Fingerprinting) im Sinne des TTDSG umfasst. Die Einwilligung
            ist jederzeit widerrufbar.
          </p>
          <p data-id="element-262">
            Die Datenübertragung in die USA wird auf die
            Standardvertragsklauseln der EU-Kommission gestützt. Details finden
            Sie hier:{' '}
            <a href="https://webflow.com/legal/eu-privacy-policy" className="text-orange-400 hover:text-orange-300" data-id="element-263">
              https://webflow.com/legal/eu-privacy-policy
            </a>
            .
          </p>
        </section>

        <section className="space-y-4" data-id="element-264">
          <h3 className="text-xl font-bold text-white" data-id="element-265">
            3. Allgemeine Hinweise und Pflichtinformationen
          </h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-266">Datenschutz</h4>
          <p data-id="element-267">
            Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen
            Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten
            vertraulich und entsprechend den gesetzlichen
            Datenschutzvorschriften sowie dieser Datenschutzerklärung.
          </p>
          <p data-id="element-268">
            Wenn Sie diese Website benutzen, werden verschiedene
            personenbezogene Daten erhoben. Personenbezogene Daten sind Daten,
            mit denen Sie persönlich identifiziert werden können. Die
            vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben
            und wofür wir sie nutzen. Sie erläutert auch, wie und zu welchem
            Zweck das geschieht.
          </p>
          <p data-id="element-269">
            Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B.
            bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann.
            Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist
            nicht möglich.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-270">
            Hinweis zur verantwortlichen Stelle
          </h4>
          <p data-id="element-271">
            Die verantwortliche Stelle für die Datenverarbeitung auf dieser
            Website ist:
          </p>
          <p data-id="element-272">
            CRM Kraft LLC
            <br data-id="element-273" />
            Vertreten durch: Justus Kraft
            <br data-id="element-274" />
            7901 4th St. N, Ste. 300
            <br data-id="element-275" />
            St. Petersburg, FL 33702
            <br data-id="element-276" />
            United States of America
          </p>
          <p data-id="element-277">
            Telefon: +49 157 35999071
            <br data-id="element-278" />
            E-Mail: justus@crm-kraft.de
          </p>
          <p data-id="element-279">
            Verantwortliche Stelle ist die natürliche oder juristische Person,
            die allein oder gemeinsam mit anderen über die Zwecke und Mittel der
            Verarbeitung von personenbezogenen Daten (z. B. Namen,
            E-Mail-Adressen o. Ä.) entscheidet.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-280">Speicherdauer</h4>
          <p data-id="element-281">
            Soweit innerhalb dieser Datenschutzerklärung keine speziellere
            Speicherdauer genannt wurde, verbleiben Ihre personenbezogenen Daten
            bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie
            ein berechtigtes Löschersuchen geltend machen oder eine Einwilligung
            zur Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, sofern
            wir keine anderen rechtlich zulässigen Gründe für die Speicherung
            Ihrer personenbezogenen Daten haben (z. B. steuer- oder
            handelsrechtliche Aufbewahrungsfristen); im letztgenannten Fall
            erfolgt die Löschung nach Fortfall dieser Gründe.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-282">
            Allgemeine Hinweise zu den Rechtsgrundlagen der Datenverarbeitung
            auf dieser Website
          </h4>
          <p data-id="element-283">
            Sofern Sie in die Datenverarbeitung eingewilligt haben, verarbeiten
            wir Ihre personenbezogenen Daten auf Grundlage von Art. 6 Abs. 1
            lit. a DSGVO bzw. Art. 9 Abs. 2 lit. a DSGVO, sofern besondere
            Datenkategorien nach Art. 9 Abs. 1 DSGVO verarbeitet werden. Im
            Falle einer ausdrücklichen Einwilligung in die Übertragung
            personenbezogener Daten in Drittstaaten erfolgt die
            Datenverarbeitung außerdem auf Grundlage von Art. 49 Abs. 1 lit. a
            DSGVO. Sofern Sie in die Speicherung von Cookies oder in den Zugriff
            auf Informationen in Ihr Endgerät (z. B. via Device-Fingerprinting)
            eingewilligt haben, erfolgt die Datenverarbeitung zusätzlich auf
            Grundlage von § 25 Abs. 1 TTDSG. Die Einwilligung ist jederzeit
            widerrufbar. Sind Ihre Daten zur Vertragserfüllung oder zur
            Durchführung vorvertraglicher Maßnahmen erforderlich, verarbeiten
            wir Ihre Daten auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO. Des
            Weiteren verarbeiten wir Ihre Daten, sofern diese zur Erfüllung
            einer rechtlichen Verpflichtung erforderlich sind auf Grundlage von
            Art. 6 Abs. 1 lit. c DSGVO. Die Datenverarbeitung kann ferner auf
            Grundlage unseres berechtigten Interesses nach Art. 6 Abs. 1 lit. f
            DSGVO erfolgen. Über die jeweils im Einzelfall einschlägigen
            Rechtsgrundlagen wird in den folgenden Absätzen dieser
            Datenschutzerklärung informiert.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-284">
            Hinweis zur Datenweitergabe in die USA und sonstige Drittstaaten
          </h4>
          <p data-id="element-285">
            Wir verwenden unter anderem Tools von Unternehmen mit Sitz in den
            USA oder sonstigen datenschutzrechtlich nicht sicheren Drittstaaten.
            Wenn diese Tools aktiv sind, können Ihre personenbezogene Daten in
            diese Drittstaaten übertragen und dort verarbeitet werden. Wir
            weisen darauf hin, dass in diesen Ländern kein mit der EU
            vergleichbares Datenschutzniveau garantiert werden kann.
            Beispielsweise sind US-Unternehmen dazu verpflichtet,
            personenbezogene Daten an Sicherheitsbehörden herauszugeben, ohne
            dass Sie als Betroffener hiergegen gerichtlich vorgehen könnten. Es
            kann daher nicht ausgeschlossen werden, dass US-Behörden (z. B.
            Geheimdienste) Ihre auf US-Servern befindlichen Daten zu
            Überwachungszwecken verarbeiten, auswerten und dauerhaft speichern.
            Wir haben auf diese Verarbeitungstätigkeiten keinen Einfluss.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-286">
            Widerruf Ihrer Einwilligung zur Datenverarbeitung
          </h4>
          <p data-id="element-287">
            Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen
            Einwilligung möglich. Sie können eine bereits erteilte Einwilligung
            jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf
            erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-288">
            Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen sowie
            gegen Direktwerbung (Art. 21 DSGVO)
          </h4>
          <p className="uppercase text-xs sm:text-sm bg-slate-800 p-4 rounded-lg border border-slate-700" data-id="element-289">
            WENN DIE DATENVERARBEITUNG AUF GRUNDLAGE VON ART. 6 ABS. 1 LIT. E
            ODER F DSGVO ERFOLGT, HABEN SIE JEDERZEIT DAS RECHT, AUS GRÜNDEN,
            DIE SICH AUS IHRER BESONDEREN SITUATION ERGEBEN, GEGEN DIE
            VERARBEITUNG IHRER PERSONENBEZOGENEN DATEN WIDERSPRUCH EINZULEGEN;
            DIES GILT AUCH FÜR EIN AUF DIESE BESTIMMUNGEN GESTÜTZTES PROFILING.
            DIE JEWEILIGE RECHTSGRUNDLAGE, AUF DENEN EINE VERARBEITUNG BERUHT,
            ENTNEHMEN SIE DIESER DATENSCHUTZERKLÄRUNG. WENN SIE WIDERSPRUCH
            EINLEGEN, WERDEN WIR IHRE BETROFFENEN PERSONENBEZOGENEN DATEN NICHT
            MEHR VERARBEITEN, ES SEI DENN, WIR KÖNNEN ZWINGENDE SCHUTZWÜRDIGE
            GRÜNDE FÜR DIE VERARBEITUNG NACHWEISEN, DIE IHRE INTERESSEN, RECHTE
            UND FREIHEITEN ÜBERWIEGEN ODER DIE VERARBEITUNG DIENT DER
            GELTENDMACHUNG, AUSÜBUNG ODER VERTEIDIGUNG VON RECHTSANSPRÜCHEN
            (WIDERSPRUCH NACH ART. 21 ABS. 1 DSGVO).
          </p>
          <p className="uppercase text-xs sm:text-sm bg-slate-800 p-4 rounded-lg border border-slate-700 mt-4" data-id="element-290">
            WERDEN IHRE PERSONENBEZOGENEN DATEN VERARBEITET, UM DIREKTWERBUNG ZU
            BETREIBEN, SO HABEN SIE DAS RECHT, JEDERZEIT WIDERSPRUCH GEGEN DIE
            VERARBEITUNG SIE BETREFFENDER PERSONENBEZOGENER DATEN ZUM ZWECKE
            DERARTIGER WERBUNG EINZULEGEN; DIES GILT AUCH FÜR DAS PROFILING,
            SOWEIT ES MIT SOLCHER DIREKTWERBUNG IN VERBINDUNG STEHT. WENN SIE
            WIDERSPRECHEN, WERDEN IHRE PERSONENBEZOGENEN DATEN ANSCHLIESSEND
            NICHT MEHR ZUM ZWECKE DER DIREKTWERBUNG VERWENDET (WIDERSPRUCH NACH
            ART. 21 ABS. 2 DSGVO).
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-291">
            Beschwerderecht bei der zuständigen Aufsichtsbehörde
          </h4>
          <p data-id="element-292">
            Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein
            Beschwerderecht bei einer Aufsichtsbehörde, insbesondere in dem
            Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes
            oder des Orts des mutmaßlichen Verstoßes zu. Das Beschwerderecht
            besteht unbeschadet anderweitiger verwaltungsrechtlicher oder
            gerichtlicher Rechtsbehelfe.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-293">
            Recht auf Datenübertragbarkeit
          </h4>
          <p data-id="element-294">
            Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung
            oder in Erfüllung eines Vertrags automatisiert verarbeiten, an sich
            oder an einen Dritten in einem gängigen, maschinenlesbaren Format
            aushändigen zu lassen. Sofern Sie die direkte Übertragung der Daten
            an einen anderen Verantwortlichen verlangen, erfolgt dies nur,
            soweit es technisch machbar ist.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-295">
            Auskunft, Löschung und Berichtigung
          </h4>
          <p data-id="element-296">
            Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen
            jederzeit das Recht auf unentgeltliche Auskunft über Ihre
            gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger
            und den Zweck der Datenverarbeitung und ggf. ein Recht auf
            Berichtigung oder Löschung dieser Daten. Hierzu sowie zu weiteren
            Fragen zum Thema personenbezogene Daten können Sie sich jederzeit an
            uns wenden.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-297">
            Recht auf Einschränkung der Verarbeitung
          </h4>
          <p data-id="element-298">
            Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer
            personenbezogenen Daten zu verlangen. Hierzu können Sie sich
            jederzeit an uns wenden. Das Recht auf Einschränkung der
            Verarbeitung besteht in folgenden Fällen:
          </p>
          <ul className="list-disc pl-5 space-y-2" data-id="element-299">
            <li data-id="element-300">
              Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten
              personenbezogenen Daten bestreiten, benötigen wir in der Regel
              Zeit, um dies zu überprüfen. Für die Dauer der Prüfung haben Sie
              das Recht, die Einschränkung der Verarbeitung Ihrer
              personenbezogenen Daten zu verlangen.
            </li>
            <li data-id="element-301">
              Wenn die Verarbeitung Ihrer personenbezogenen Daten unrechtmäßig
              geschah/geschieht, können Sie statt der Löschung die Einschränkung
              der Datenverarbeitung verlangen.
            </li>
            <li data-id="element-302">
              Wenn wir Ihre personenbezogenen Daten nicht mehr benötigen, Sie
              sie jedoch zur Ausübung, Verteidigung oder Geltendmachung von
              Rechtsansprüchen benötigen, haben Sie das Recht, statt der
              Löschung die Einschränkung der Verarbeitung Ihrer
              personenbezogenen Daten zu verlangen.
            </li>
            <li data-id="element-303">
              Wenn Sie einen Widerspruch nach Art. 21 Abs. 1 DSGVO eingelegt
              haben, muss eine Abwägung zwischen Ihren und unseren Interessen
              vorgenommen werden. Solange noch nicht feststeht, wessen
              Interessen überwiegen, haben Sie das Recht, die Einschränkung der
              Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
            </li>
          </ul>
          <p data-id="element-304">
            Wenn Sie die Verarbeitung Ihrer personenbezogenen Daten
            eingeschränkt haben, dürfen diese Daten – von ihrer Speicherung
            abgesehen – nur mit Ihrer Einwilligung oder zur Geltendmachung,
            Ausübung oder Verteidigung von Rechtsansprüchen oder zum Schutz der
            Rechte einer anderen natürlichen oder juristischen Person oder aus
            Gründen eines wichtigen öffentlichen Interesses der Europäischen
            Union oder eines Mitgliedstaats verarbeitet werden.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-305">
            SSL- bzw. TLS-Verschlüsselung
          </h4>
          <p data-id="element-306">
            Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der
            Übertragung vertraulicher Inhalte, wie zum Beispiel Bestellungen
            oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL-
            bzw. TLS Verschlüsselung. Eine verschlüsselte Verbindung erkennen
            Sie daran, dass die Adresszeile des Browsers von „http://“ auf
            „https://“ wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
          </p>
          <p data-id="element-307">
            Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die
            Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen
            werden.
          </p>
        </section>

        <section className="space-y-4" data-id="element-308">
          <h3 className="text-xl font-bold text-white" data-id="element-309">
            4. Datenerfassung auf dieser Website
          </h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-310">Cookies</h4>
          <p data-id="element-311">
            Unsere Internetseiten verwenden so genannte „Cookies“. Cookies sind
            kleine Textdateien und richten auf Ihrem Endgerät keinen Schaden an.
            Sie werden entweder vorübergehend für die Dauer einer Sitzung
            (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem
            Endgerät gespeichert. Session-Cookies werden nach Ende Ihres Besuchs
            automatisch gelöscht. Permanente Cookies bleiben auf Ihrem Endgerät
            gespeichert, bis Sie diese selbst löschen oder eine automatische
            Löschung durch Ihren Webbrowser erfolgt.
          </p>
          <p data-id="element-312">
            Teilweise können auch Cookies von Drittunternehmen auf Ihrem
            Endgerät gespeichert werden, wenn Sie unsere Seite betreten
            (Third-Party-Cookies). Diese ermöglichen uns oder Ihnen die Nutzung
            bestimmter Dienstleistungen des Drittunternehmens (z. B. Cookies zur
            Abwicklung von Zahlungsdienstleistungen).
          </p>
          <p data-id="element-313">
            Cookies haben verschiedene Funktionen. Zahlreiche Cookies sind
            technisch notwendig, da bestimmte Websitefunktionen ohne diese nicht
            funktionieren würden (z. B. die Warenkorbfunktion oder die Anzeige
            von Videos). Andere Cookies dienen dazu, das Nutzerverhalten
            auszuwerten oder Werbung anzuzeigen.
          </p>
          <p data-id="element-314">
            Cookies, die zur Durchführung des elektronischen
            Kommunikationsvorgangs, zur Bereitstellung bestimmter, von Ihnen
            erwünschter Funktionen (z. B. für die Warenkorbfunktion) oder zur
            Optimierung der Website (z. B. Cookies zur Messung des Webpublikums)
            erforderlich sind (notwendige Cookies), werden auf Grundlage von
            Art. 6 Abs. 1 lit. f DSGVO gespeichert, sofern keine andere
            Rechtsgrundlage angegeben wird. Der Websitebetreiber hat ein
            berechtigtes Interesse an der Speicherung von notwendigen Cookies
            zur technisch fehlerfreien und optimierten Bereitstellung seiner
            Dienste. Sofern eine Einwilligung zur Speicherung von Cookies und
            vergleichbaren Wiedererkennungstechnologien abgefragt wurde, erfolgt
            die Verarbeitung ausschließlich auf Grundlage dieser Einwilligung
            (Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TTDSG); die Einwilligung
            ist jederzeit widerrufbar.
          </p>
          <p data-id="element-315">
            Sie können Ihren Browser so einstellen, dass Sie über das Setzen von
            Cookies informiert werden und Cookies nur im Einzelfall erlauben,
            die Annahme von Cookies für bestimmte Fälle oder generell
            ausschließen sowie das automatische Löschen der Cookies beim
            Schließen des Browsers aktivieren. Bei der Deaktivierung von Cookies
            kann die Funktionalität dieser Website eingeschränkt sein.
          </p>
          <p data-id="element-316">
            Soweit Cookies von Drittunternehmen oder zu Analysezwecken
            eingesetzt werden, werden wir Sie hierüber im Rahmen dieser
            Datenschutzerklärung gesondert informieren und ggf. eine
            Einwilligung abfragen.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-317">Kontaktformular</h4>
          <p data-id="element-318">
            Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden
            Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort
            angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den
            Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir
            nicht ohne Ihre Einwilligung weiter.
          </p>
          <p data-id="element-319">
            Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs.
            1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags
            zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen
            erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung
            auf unserem berechtigten Interesse an der effektiven Bearbeitung der
            an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf
            Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) sofern diese
            abgefragt wurde; die Einwilligung ist jederzeit widerrufbar.
          </p>
          <p data-id="element-320">
            Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei
            uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur
            Speicherung widerrufen oder der Zweck für die Datenspeicherung
            entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage).
            Zwingende gesetzliche Bestimmungen – insbesondere
            Aufbewahrungsfristen – bleiben unberührt.
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-321">
            Anfrage per E-Mail, Telefon oder Telefax
          </h4>
          <p data-id="element-322">
            Wenn Sie uns per E-Mail, Telefon oder Telefax kontaktieren, wird
            Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen
            Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei
            uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne
            Ihre Einwilligung weiter.
          </p>
          <p data-id="element-323">
            Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs.
            1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags
            zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen
            erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung
            auf unserem berechtigten Interesse an der effektiven Bearbeitung der
            an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf
            Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) sofern diese
            abgefragt wurde; die Einwilligung ist jederzeit widerrufbar.
          </p>
          <p data-id="element-324">
            Die von Ihnen an uns per Kontaktanfragen übersandten Daten
            verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre
            Einwilligung zur Speicherung widerrufen oder der Zweck für die
            Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung
            Ihres Anliegens). Zwingende gesetzliche Bestimmungen – insbesondere
            gesetzliche Aufbewahrungsfristen – bleiben unberührt.
          </p>
        </section>

        <section className="space-y-4" data-id="element-325">
          <h3 className="text-xl font-bold text-white" data-id="element-326">
            5. Analyse-Tools und Werbung
          </h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-327">
            IONOS WebAnalytics
          </h4>
          <p data-id="element-328">
            Diese Website nutzt die Analysedienste von IONOS WebAnalytics (im
            Folgenden: IONOS). Anbieter ist die 1&1 IONOS SE, Elgendorfer Straße
            57, D – 56410 Montabaur. Im Rahmen der Analysen mit IONOS können u.
            a. Besucherzahlen und –verhalten (z. B. Anzahl der Seitenaufrufe,
            Dauer eines Webseitenbesuchs, Absprungraten), Besucherquellen (d.
            h., von welcher Seite der Besucher kommt), Besucherstandorte sowie
            technische Daten (Browser- und Betriebssystemversionen) analysiert
            werden. Zu diesem Zweck speichert IONOS insbesondere folgende Daten:
          </p>
          <ul className="list-disc pl-5 space-y-2" data-id="element-329">
            <li data-id="element-330">Referrer (zuvor besuchte Webseite)</li>
            <li data-id="element-331">angeforderte Webseite oder Datei</li>
            <li data-id="element-332">Browsertyp und Browserversion</li>
            <li data-id="element-333">verwendetes Betriebssystem</li>
            <li data-id="element-334">verwendeter Gerätetyp</li>
            <li data-id="element-335">Uhrzeit des Zugriffs</li>
            <li data-id="element-336">
              IP-Adresse in anonymisierter Form (wird nur zur Feststellung des
              Orts des Zugriffs verwendet)
            </li>
          </ul>
          <p data-id="element-337">
            Die Datenerfassung erfolgt laut IONOS vollständig anonymisiert,
            sodass sie nicht zu einzelnen Personen zurückverfolgt werden kann.
            Cookies werden von IONOS WebAnalytics nicht gespeichert.
          </p>
          <p data-id="element-338">
            Die Speicherung und Analyse der Daten erfolgt auf Grundlage von Art.
            6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes
            Interesse an der statistischen Analyse des Nutzerverhaltens, um
            sowohl sein Webangebot als auch seine Werbung zu optimieren. Sofern
            eine entsprechende Einwilligung abgefragt wurde, erfolgt die
            Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a
            DSGVO und § 25 Abs. 1 TTDSG, soweit die Einwilligung die Speicherung
            von Cookies oder den Zugriff auf Informationen im Endgerät des
            Nutzers (z. B. Device-Fingerprinting) im Sinne des TTDSG umfasst.
            Die Einwilligung ist jederzeit widerrufbar.
          </p>
          <p data-id="element-339">
            Weitere Informationen zur Datenerfassung und Verarbeitung durch
            IONOS WebAnalytics entnehmen Sie der Datenschutzerklaerung von IONOS
            unter folgendem Link:{' '}
            <a href="https://www.ionos.de/terms-gtc/index.php?id=6" className="text-orange-400 hover:text-orange-300" data-id="element-340">
              https://www.ionos.de/terms-gtc/index.php?id=6
            </a>
          </p>
        </section>

        <section className="space-y-4" data-id="element-341">
          <h3 className="text-xl font-bold text-white" data-id="element-342">6. Newsletter</h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-343">Newsletterdaten</h4>
          <p data-id="element-344">
            Wenn Sie den auf der Website angebotenen Newsletter beziehen
            möchten, benötigen wir von Ihnen eine E Mail-Adresse sowie
            Informationen, welche uns die Überprüfung gestatten, dass Sie der
            Inhaber der angegebenen E-Mail-Adresse sind und mit dem Empfang des
            Newsletters einverstanden sind. Weitere Daten werden nicht bzw. nur
            auf freiwilliger Basis erhoben. Diese Daten verwenden wir
            ausschließlich für den Versand der angeforderten Informationen und
            geben diese nicht an Dritte weiter.
          </p>
          <p data-id="element-345">
            Die Verarbeitung der in das Newsletteranmeldeformular eingegebenen
            Daten erfolgt ausschließlich auf Grundlage Ihrer Einwilligung (Art.
            6 Abs. 1 lit. a DSGVO). Die erteilte Einwilligung zur Speicherung
            der Daten, der E-Mail-Adresse sowie deren Nutzung zum Versand des
            Newsletters können Sie jederzeit widerrufen, etwa über den
            „Austragen“-Link im Newsletter. Die Rechtmäßigkeit der bereits
            erfolgten Datenverarbeitungsvorgänge bleibt vom Widerruf unberührt.
          </p>
          <p data-id="element-346">
            Die von Ihnen zum Zwecke des Newsletter-Bezugs bei uns hinterlegten
            Daten werden von uns bis zu Ihrer Austragung aus dem Newsletter bei
            uns bzw. dem Newsletterdiensteanbieter gespeichert und nach der
            Abbestellung des Newsletters oder nach Zweckfortfall aus der
            Newsletterverteilerliste gelöscht. Wir behalten uns vor,
            E-Mail-Adressen aus unserem Newsletterverteiler nach eigenem
            Ermessen im Rahmen unseres berechtigten Interesses nach Art. 6 Abs.
            1 lit. f DSGVO zu löschen oder zu sperren.
          </p>
          <p data-id="element-347">
            Daten, die zu anderen Zwecken bei uns gespeichert wurden, bleiben
            hiervon unberührt.
          </p>
          <p data-id="element-348">
            Nach Ihrer Austragung aus der Newsletterverteilerliste wird Ihre
            E-Mail-Adresse bei uns bzw. dem Newsletterdiensteanbieter ggf. in
            einer Blacklist gespeichert, sofern dies zur Verhinderung künftiger
            Mailings erforderlich ist. Die Daten aus der Blacklist werden nur
            für diesen Zweck verwendet und nicht mit anderen Daten
            zusammengeführt. Dies dient sowohl Ihrem Interesse als auch unserem
            Interesse an der Einhaltung der gesetzlichen Vorgaben beim Versand
            von Newslettern (berechtigtes Interesse im Sinne des Art. 6 Abs. 1
            lit. f DSGVO). Die Speicherung in der Blacklist ist zeitlich nicht
            befristet. Sie können der Speicherung widersprechen, sofern Ihre
            Interessen unser berechtigtes Interesse überwiegen.
          </p>
        </section>

        <section className="space-y-4" data-id="element-349">
          <h3 className="text-xl font-bold text-white" data-id="element-350">7. Plugins und Tools</h3>

          <h4 className="text-lg font-semibold text-white" data-id="element-351">YouTube</h4>
          <p data-id="element-352">
            Diese Website bindet Videos der Website YouTube ein. Betreiber der
            Website ist die Google Ireland Limited („Google“), Gordon House,
            Barrow Street, Dublin 4, Irland.
          </p>
          <p data-id="element-353">
            Wenn Sie eine unserer Webseiten besuchen, auf denen YouTube
            eingebunden ist, wird eine Verbindung zu den Servern von YouTube
            hergestellt. Dabei wird dem YouTube-Server mitgeteilt, welche
            unserer Seiten Sie besucht haben.
          </p>
          <p data-id="element-354">
            Des Weiteren kann YouTube verschiedene Cookies auf Ihrem Endgerät
            speichern oder vergleichbare Technologien zur Wiedererkennung
            verwenden (z. B. Device-Fingerprinting). Auf diese Weise kann
            YouTube Informationen über Besucher dieser Website erhalten. Diese
            Informationen werden u. a. verwendet, um Videostatistiken zu
            erfassen, die Anwenderfreundlichkeit zu verbessern und
            Betrugsversuchen vorzubeugen.
          </p>
          <p data-id="element-355">
            Wenn Sie in Ihrem YouTube-Account eingeloggt sind, ermöglichen Sie
            YouTube, Ihr Surfverhalten direkt Ihrem persönlichen Profil
            zuzuordnen. Dies können Sie verhindern, indem Sie sich aus Ihrem
            YouTube Account ausloggen.
          </p>
          <p data-id="element-356">
            Die Nutzung von YouTube erfolgt im Interesse einer ansprechenden
            Darstellung unserer Online-Angebote. Dies stellt ein berechtigtes
            Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar. Sofern eine
            entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung
            ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25
            Abs. 1 TTDSG, soweit die Einwilligung die Speicherung von Cookies
            oder den Zugriff auf Informationen im Endgerät des Nutzers (z. B.
            Device-Fingerprinting) im Sinne des TTDSG umfasst. Die Einwilligung
            ist jederzeit widerrufbar.
          </p>
          <p data-id="element-357">
            Weitere Informationen zum Umgang mit Nutzerdaten finden Sie in der
            Datenschutzerklärung von YouTube unter:{' '}
            <a href="https://policies.google.com/privacy?hl=de" className="text-orange-400 hover:text-orange-300" data-id="element-358">
              https://policies.google.com/privacy?hl=de
            </a>
            .
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-359">Vimeo</h4>
          <p data-id="element-360">
            Diese Website nutzt Plugins des Videoportals Vimeo. Anbieter ist die
            Vimeo Inc., 555 West 18th Street, New York, New York 10011, USA.
          </p>
          <p data-id="element-361">
            Wenn Sie eine unserer mit einem Vimeo-Video ausgestatteten Seiten
            besuchen, wird eine Verbindung zu den Servern von Vimeo hergestellt.
            Dabei wird dem Vimeo-Server mitgeteilt, welche unserer Seiten Sie
            besucht haben. Zudem erlangt Vimeo Ihre IP-Adresse. Dies gilt auch
            dann, wenn Sie nicht bei Vimeo eingeloggt sind oder keinen Account
            bei Vimeo besitzen. Die von Vimeo erfassten Informationen werden an
            den Vimeo-Server in den USA übermittelt.
          </p>
          <p data-id="element-362">
            Wenn Sie in Ihrem Vimeo-Account eingeloggt sind, ermöglichen Sie
            Vimeo, Ihr Surfverhalten direkt Ihrem persönlichen Profil
            zuzuordnen. Dies können Sie verhindern, indem Sie sich aus Ihrem
            Vimeo-Account ausloggen.
          </p>
          <p data-id="element-363">
            Zur Wiedererkennung der Websitebesucher verwendet Vimeo Cookies bzw.
            vergleichbare Wiedererkennungstechnologien (z. B.
            Device-Fingerprinting).
          </p>
          <p data-id="element-364">
            Die Nutzung von Vimeo erfolgt im Interesse einer ansprechenden
            Darstellung unserer Online-Angebote. Dies stellt ein berechtigtes
            Interesse im Sinne des Art. 6 Abs. 1 lit. f DSGVO dar. Sofern eine
            entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung
            ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25
            Abs. 1 TTDSG, soweit die Einwilligung die Speicherung von Cookies
            oder den Zugriff auf Informationen im Endgerät des Nutzers (z. B.
            Device-Fingerprinting) im Sinne des TTDSG umfasst. Die Einwilligung
            ist jederzeit widerrufbar.
          </p>
          <p data-id="element-365">
            Die Datenübertragung in die USA wird auf die
            Standardvertragsklauseln der EU-Kommission sowie nach Aussage von
            Vimeo auf „berechtigte Geschäftsinteressen" gestützt. Details finden
            Sie hier:{' '}
            <a href="https://vimeo.com/privacy" className="text-orange-400 hover:text-orange-300" data-id="element-366">
              https://vimeo.com/privacy
            </a>
            .
          </p>
          <p data-id="element-367">
            Weitere Informationen zum Umgang mit Nutzerdaten finden Sie in der
            Datenschutzerklärung von Vimeo unter:{' '}
            <a href="https://vimeo.com/privacy" className="text-orange-400 hover:text-orange-300" data-id="element-368">
              https://vimeo.com/privacy
            </a>
            .
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-369">Google Web Fonts</h4>
          <p data-id="element-370">
            Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten so
            genannte Web Fonts, die von Google bereitgestellt werden. Beim
            Aufruf einer Seite lädt Ihr Browser die benötigten Web Fonts in
            ihren Browsercache, um Texte und Schriftarten korrekt anzuzeigen.
          </p>
          <p data-id="element-371">
            Zu diesem Zweck muss der von Ihnen verwendete Browser Verbindung zu
            den Servern von Google aufnehmen. Hierdurch erlangt Google Kenntnis
            darüber, dass über Ihre IP-Adresse diese Website aufgerufen wurde.
            Die Nutzung von Google WebFonts erfolgt auf Grundlage von Art. 6
            Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes
            Interesse an der einheitlichen Darstellung des Schriftbildes auf
            seiner Website. Sofern eine entsprechende Einwilligung abgefragt
            wurde, erfolgt die Verarbeitung ausschließlich auf Grundlage von
            Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TTDSG, soweit die
            Einwilligung die Speicherung von Cookies oder den Zugriff auf
            Informationen im Endgerät des Nutzers (z. B. Device Fingerprinting)
            im Sinne des TTDSG umfasst. Die Einwilligung ist jederzeit
            widerrufbar.
          </p>
          <p data-id="element-372">
            Wenn Ihr Browser Web Fonts nicht unterstützt, wird eine
            Standardschrift von Ihrem Computer genutzt.
          </p>
          <p data-id="element-373">
            Weitere Informationen zu Google Web Fonts finden Sie unter{' '}
            <a href="https://developers.google.com/fonts/faq" className="text-orange-400 hover:text-orange-300" data-id="element-374">
              https://developers.google.com/fonts/faq
            </a>{' '}
            und in der Datenschutzerklärung von Google:{' '}
            <a href="https://policies.google.com/privacy?hl=de" className="text-orange-400 hover:text-orange-300" data-id="element-375">
              https://policies.google.com/privacy?hl=de
            </a>
            .
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-376">Google Maps</h4>
          <p data-id="element-377">
            Diese Seite nutzt den Kartendienst Google Maps. Anbieter ist die
            Google Ireland Limited („Google“), Gordon House, Barrow Street,
            Dublin 4, Irland.
          </p>
          <p data-id="element-378">
            Zur Nutzung der Funktionen von Google Maps ist es notwendig, Ihre
            IP-Adresse zu speichern. Diese Informationen werden in der Regel an
            einen Server von Google in den USA übertragen und dort gespeichert.
          </p>
          <p data-id="element-379">
            Der Anbieter dieser Seite hat keinen Einfluss auf diese
            Datenübertragung. Wenn Google Maps aktiviert ist, kann Google zum
            Zwecke der einheitlichen Darstellung der Schriftarten Google Web
            Fonts verwenden. Beim Aufruf von Google Maps lädt Ihr Browser die
            benötigten Web Fonts in ihren Browsercache, um Texte und
            Schriftarten korrekt anzuzeigen.
          </p>
          <p data-id="element-380">
            Die Nutzung von Google Maps erfolgt im Interesse einer ansprechenden
            Darstellung unserer Online Angebote und an einer leichten
            Auffindbarkeit der von uns auf der Website angegebenen Orte. Dies
            stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f
            DSGVO dar. Sofern eine entsprechende Einwilligung abgefragt wurde,
            erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6
            Abs. 1 lit. a DSGVO und § 25 Abs. 1 TTDSG, soweit die Einwilligung
            die Speicherung von Cookies oder den Zugriff auf Informationen im
            Endgerät des Nutzers (z. B. Device-Fingerprinting) im Sinne des
            TTDSG umfasst. Die Einwilligung ist jederzeit widerrufbar.
          </p>
          <p data-id="element-381">
            Die Datenübertragung in die USA wird auf die
            Standardvertragsklauseln der EU-Kommission gestützt. Details finden
            Sie hier:{' '}
            <a href="https://privacy.google.com/businesses/gdprcontrollerterms/" className="text-orange-400 hover:text-orange-300" data-id="element-382">
              https://privacy.google.com/businesses/gdprcontrollerterms/
            </a>{' '}
            und{' '}
            <a href="https://privacy.google.com/businesses/gdprcontrollerterms/sccs/" className="text-orange-400 hover:text-orange-300" data-id="element-383">
              https://privacy.google.com/businesses/gdprcontrollerterms/sccs/
            </a>
            .
          </p>
          <p data-id="element-384">
            Mehr Informationen zum Umgang mit Nutzerdaten finden Sie in der
            Datenschutzerklärung von Google:{' '}
            <a href="https://policies.google.com/privacy?hl=de" className="text-orange-400 hover:text-orange-300" data-id="element-385">
              https://policies.google.com/privacy?hl=de
            </a>
            .
          </p>

          <h4 className="text-lg font-semibold text-white" data-id="element-386">Google reCAPTCHA</h4>
          <p data-id="element-387">
            Wir nutzen „Google reCAPTCHA" (im Folgenden „reCAPTCHA") auf dieser
            Website. Anbieter ist die Google Ireland Limited („Google"), Gordon
            House, Barrow Street, Dublin 4, Irland.
          </p>
          <p data-id="element-388">
            Mit reCAPTCHA soll überprüft werden, ob die Dateneingabe auf dieser
            Website (z. B. in einem Kontaktformular) durch einen Menschen oder
            durch ein automatisiertes Programm erfolgt. Hierzu analysiert
            reCAPTCHA das Verhalten des Websitebesuchers anhand verschiedener
            Merkmale. Diese Analyse beginnt automatisch, sobald der
            Websitebesucher die Website betritt. Zur Analyse wertet reCAPTCHA
            verschiedene Informationen aus (z. B. IP-Adresse, Verweildauer des
            Websitebesuchers auf der Website oder vom Nutzer getätigte
            Mausbewegungen). Die bei der Analyse erfassten Daten werden an
            Google weitergeleitet.
          </p>
          <p data-id="element-389">
            Die reCAPTCHA-Analysen laufen vollständig im Hintergrund.
            Websitebesucher werden nicht darauf hingewiesen, dass eine Analyse
            stattfindet.
          </p>
          <p data-id="element-390">
            Die Speicherung und Analyse der Daten erfolgt auf Grundlage von Art.
            6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes
            Interesse daran, seine Webangebote vor missbräuchlicher
            automatisierter Ausspähung und vor SPAM zu schützen. Sofern eine
            entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung
            ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25
            Abs. 1 TTDSG, soweit die Einwilligung die Speicherung von Cookies
            oder den Zugriff auf Informationen im Endgerät des Nutzers (z. B.
            Device-Fingerprinting) im Sinne des TTDSG umfasst. Die Einwilligung
            ist jederzeit widerrufbar.
          </p>
          <p data-id="element-391">
            Weitere Informationen zu Google reCAPTCHA entnehmen Sie den
            Google-Datenschutzbestimmungen und den Google Nutzungsbedingungen
            unter folgenden Links:{' '}
            <a href="https://policies.google.com/privacy?hl=de" className="text-orange-400 hover:text-orange-300" data-id="element-392">
              https://policies.google.com/privacy?hl=de
            </a>{' '}
            und{' '}
            <a href="https://policies.google.com/terms?hl=de" className="text-orange-400 hover:text-orange-300" data-id="element-393">
              https://policies.google.com/terms?hl=de
            </a>
            .
          </p>
        </section>
      </div>
    </ModalLayout>;
}